import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { MessageSquare, Star, EyeOff, Eye } from 'lucide-react';
import { adminApi, formatNumber, formatDateTime, extractErrorMessage } from '@kardume/services';
import { theme } from '@kardume/theme';
import type { ReviewDto, ReviewStatus, Tone } from '@kardume/types';
import { useNotification } from '@kardume/hooks';
import {
  PageHeader,
  StatCard,
  Card,
  CardBody,
  Badge,
  Button,
  Modal,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const FilterChip = styled.button<{ $active: boolean }>`
  padding: 0.4rem 0.85rem;
  font-size: 0.85rem;
  font-family: inherit;
  cursor: pointer;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? p.theme.primary : p.theme.border)};
  background: ${(p) => (p.$active ? p.theme.primary : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.textInverted : p.theme.textSecondary)};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(p) => p.theme.primary};
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReviewHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const Author = styled.div`
  strong {
    color: ${(p) => p.theme.textPrimary};
  }
  span {
    display: block;
    font-size: 0.8rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Stars = styled.div`
  color: ${(p) => p.theme.warning};
  font-size: 0.95rem;
  letter-spacing: 0.05em;
`;

const Comment = styled.p`
  margin: 0.5rem 0 0.75rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Reply = styled.div`
  background: ${(p) => p.theme.surfaceAlt};
  border-left: 3px solid ${(p) => p.theme.primary};
  border-radius: ${(p) => p.theme.radius};
  padding: 0.75rem 0.9rem;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textSecondary};

  strong {
    display: block;
    margin-bottom: 0.25rem;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

const STATUS_LABELS: Record<ReviewStatus, string> = {
  PUBLISHED: 'Publicada',
  PENDING: 'Pendente',
  HIDDEN: 'Oculta',
  REPORTED: 'Denunciada',
};

type StatusFilter = 'ALL' | ReviewStatus;

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'ALL', label: 'Todas' },
  { key: 'PUBLISHED', label: 'Publicadas' },
  { key: 'REPORTED', label: 'Denunciadas' },
  { key: 'HIDDEN', label: 'Ocultas' },
];

/** Map a review status to a Badge tone. */
function statusTone(status: ReviewStatus): Tone {
  if (status === 'PUBLISHED') return 'success';
  if (status === 'PENDING') return 'warning';
  return 'danger';
}

/** Merchant panel screen for reading, replying to and moderating customer reviews. */
const ReviewsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => adminApi.listReviews({ size: 100 }), []);
  const [selected, setSelected] = useState<ReviewDto | null>(null);
  const [replyText, setReplyText] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [moderatingId, setModeratingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('ALL');

  const reviews = useMemo<ReviewDto[]>(() => data?.content ?? [], [data]);
  const average = useMemo<number>(() => {
    if (reviews.length === 0) return 0;
    return reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  }, [reviews]);
  const visible = useMemo<ReviewDto[]>(
    () => (filter === 'ALL' ? reviews : reviews.filter((r) => r.status === filter)),
    [reviews, filter],
  );

  const openReply = (review: ReviewDto): void => {
    setSelected(review);
    setReplyText(review.reply ?? '');
  };

  const closeReply = (): void => {
    setSelected(null);
    setReplyText('');
  };

  const submitReply = async (): Promise<void> => {
    if (!selected || !replyText.trim() || saving) return;
    setSaving(true);
    try {
      await adminApi.replyReview(selected.id, { reply: replyText.trim() });
      showNotification('Resposta enviada com sucesso.', 'success');
      closeReply();
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível enviar a resposta.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const moderate = async (review: ReviewDto, status: ReviewStatus): Promise<void> => {
    if (moderatingId) return;
    setModeratingId(review.id);
    try {
      await adminApi.moderateReview(review.id, { status });
      showNotification(
        status === 'HIDDEN' ? 'Avaliação ocultada.' : 'Avaliação publicada.',
        'success',
      );
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível moderar a avaliação.'), 'error');
    } finally {
      setModeratingId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Avaliações"
        subtitle="Acompanhe o que seus clientes acham da sua loja e responda a eles."
      />

      <DataState
        loading={loading}
        error={error}
        onRetry={reload}
        isEmpty={reviews.length === 0}
        emptyTitle="Nenhuma avaliação ainda"
        emptyDescription="Suas avaliações aparecerão aqui assim que os clientes começarem a avaliar."
      >
        <Stats>
          <StatCard
            label="Avaliação média"
            value={`${average.toFixed(1)} ★`}
            icon={<Star size={18} />}
            iconColor={theme.warning}
          />
          <StatCard
            label="Total de avaliações"
            value={formatNumber(reviews.length)}
            icon={<MessageSquare size={18} />}
            iconColor={theme.info}
          />
        </Stats>

        <Filters>
          {FILTERS.map((f) => (
            <FilterChip key={f.key} $active={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label}
            </FilterChip>
          ))}
        </Filters>

        <List>
          {visible.map((review) => (
            <Card key={review.id}>
              <CardBody>
                <ReviewHead>
                  <Author>
                    <strong>{review.customerName}</strong>
                    <span>{formatDateTime(review.createdAt)}</span>
                    <Stars>{'★'.repeat(review.rating)}</Stars>
                  </Author>
                  <Badge tone={statusTone(review.status)}>{STATUS_LABELS[review.status]}</Badge>
                </ReviewHead>

                {review.comment && <Comment>{review.comment}</Comment>}

                {review.reply && (
                  <Reply>
                    <strong>Sua resposta</strong>
                    {review.reply}
                  </Reply>
                )}

                <Actions>
                  <Button variant="outline" onClick={() => openReply(review)}>
                    {review.reply ? 'Editar resposta' : 'Responder'}
                  </Button>

                  {review.status === 'HIDDEN' ? (
                    <Button
                      variant="ghost"
                      onClick={() => moderate(review, 'PUBLISHED')}
                      isLoading={moderatingId === review.id}
                    >
                      <Eye size={16} /> Publicar
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => moderate(review, 'HIDDEN')}
                      isLoading={moderatingId === review.id}
                    >
                      <EyeOff size={16} /> Ocultar
                    </Button>
                  )}

                  {review.status === 'REPORTED' && (
                    <Button
                      variant="ghost"
                      onClick={() => moderate(review, 'PUBLISHED')}
                      isLoading={moderatingId === review.id}
                    >
                      <Eye size={16} /> Manter publicada
                    </Button>
                  )}
                </Actions>
              </CardBody>
            </Card>
          ))}
        </List>
      </DataState>

      <Modal
        isOpen={selected !== null}
        onClose={closeReply}
        title="Responder avaliação"
        footer={
          <>
            <Button variant="ghost" onClick={closeReply}>
              Cancelar
            </Button>
            <Button onClick={submitReply} isLoading={saving} disabled={!replyText.trim()}>
              Enviar resposta
            </Button>
          </>
        }
      >
        <Textarea
          value={replyText}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReplyText(e.target.value)}
          placeholder="Escreva uma resposta para o cliente..."
        />
      </Modal>
    </>
  );
};

export default ReviewsPage;
